/**
 * Privacy-safe conversion events for the /start request funnel.
 * Pushed through the existing dataLayer (pushToDataLayer), which already
 * drops preview/internal hosts, private routes and bots.
 * Only allow-listed, non-personal properties are ever sent.
 */
import { pushToDataLayer } from './gtm';

export type ConversionEvent =
  | 'cta_click'
  | 'request_form_view'
  | 'request_form_start'
  | 'request_submit_attempt'
  | 'request_submit_success'
  | 'request_submit_error';

export const BRAND = 'bpo-contact';
const ATTR_KEYS = ['cta_id', 'type', 'service', 'industry', 'from', 'page'] as const;
const ERROR_CLASSES = ['validation', 'consent', 'email', 'rate_limit', 'server', 'network', 'unknown'] as const;
export type ErrorClass = (typeof ERROR_CLASSES)[number];

/** Short slug-like values only: anything that looks like an email/phone/free text is dropped. */
export function safeValue(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim().slice(0, 80);
  if (!s || s.includes('@') || (s.match(/\d/g) || []).length >= 7) return undefined;
  if (!/^[\w\-./%:]+$/.test(s)) return undefined;
  return s;
}

function localeFromPath(pathname: string): string {
  const m = pathname.match(/^\/([a-z]{2})(?:-[a-z]{2})?(?:\/|$)/i);
  return m ? m[1].toLowerCase() : 'en';
}

export function buildEventProps(
  pathname: string,
  search: string,
  extra: Partial<Record<(typeof ATTR_KEYS)[number], string>> = {},
): Record<string, string> {
  const q = new URLSearchParams(search);
  const out: Record<string, string> = { brand: BRAND, locale: localeFromPath(pathname), pathname: pathname.slice(0, 200) };
  for (const k of ATTR_KEYS) {
    const v = safeValue(extra[k] ?? q.get(k) ?? undefined);
    if (v) out[k] = v;
  }
  return out;
}

export function classifyError(r: { status?: number; error?: string } | unknown): ErrorClass {
  const o = (r ?? {}) as { status?: number; error?: string };
  if (o.status === 429) return 'rate_limit';
  if (o.status === 400) return /email/i.test(o.error || '') ? 'email' : 'validation';
  if (typeof o.status === 'number' && o.status >= 500) return 'server';
  if (r instanceof TypeError || /network|fetch/i.test(o.error || '')) return 'network';
  return 'unknown';
}

const once = new Set<string>();
/** Send one event. `onceKey` dedupes per page load (view/start). */
export function trackConversion(
  event: ConversionEvent,
  extra: Partial<Record<(typeof ATTR_KEYS)[number], string>> & { error_class?: ErrorClass } = {},
  onceKey?: string,
): void {
  if (typeof window === 'undefined') return;
  if (onceKey) {
    if (once.has(onceKey)) return;
    once.add(onceKey);
  }
  const { error_class, ...attrs } = extra;
  const props: Record<string, string> = buildEventProps(window.location.pathname, window.location.search, attrs);
  if (event === 'request_submit_error') props.error_class = ERROR_CLASSES.includes(error_class as ErrorClass) ? error_class! : 'unknown';
  pushToDataLayer(event, props);
}

/** CTA click on a link to /start: attribution read from the link's own query. */
export function trackCtaHref(href: string): void {
  let u: URL;
  try { u = new URL(href, window.location.origin); } catch { return; }
  if (u.origin !== window.location.origin || !/^(\/[a-z]{2}(-[a-z]{2})?)?\/start\/?$/i.test(u.pathname)) return;
  const q = u.searchParams;
  const pick = (k: string) => q.get(k) ?? undefined;
  trackConversion('cta_click', {
    cta_id: pick('cta_id'), type: pick('type'), service: pick('service'),
    industry: pick('industry'), from: pick('from'), page: pick('page'),
  });
}

/** Test helper. */
export function __resetConversionOnce(): void { once.clear(); }
